import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BarList, EmptyState, ErrorBlock, formatDate, formatNumber, LineChart,
  LoadingBlock, MetricCard, Panel, StatusPill,
} from './components';
import { rpc, supabase } from './supabase';
import { SafetyAction } from './SafetyHoldAction';
import type {
  AuditData, FeedbackStatus, IntegrationRow, IntegrationsData, MarketplaceData, OperationsData,
  OverviewData, ProductData, ProductEngagementData, ProductFeedbackData,
  ReliabilityData, ReliabilityReviewRow, SafetyData, SafetyRow, TabId, UsersData,
} from './types';

const nav: Array<{ id: TabId; label: string; icon: string }> = [
  { id: 'overview', label: 'Overview', icon: 'OV' },
  { id: 'users', label: 'Users', icon: 'US' },
  { id: 'marketplace', label: 'Marketplace', icon: 'MP' },
  { id: 'product', label: 'Product & feedback', icon: 'PR' },
  { id: 'reliability', label: 'Reliability', icon: 'RL' },
  { id: 'safety', label: 'Safety', icon: 'SF' },
  { id: 'operations', label: 'Operations', icon: 'OP' },
  { id: 'integrations', label: 'Integrations', icon: 'IN' },
  { id: 'audit', label: 'Access audit', icon: 'AU' },
];

type ViewData = OverviewData | UsersData | MarketplaceData | ProductData | ReliabilityData | SafetyData | OperationsData | IntegrationsData | AuditData;

const USERS_PAGE_SIZE = 50;

export function App() {
  const [tab, setTab] = useState<TabId>('overview');
  const [days, setDays] = useState(30);
  const [integrationHours, setIntegrationHours] = useState(24);
  const [data, setData] = useState<Partial<Record<TabId, ViewData>>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [userOffset, setUserOffset] = useState(0);
  const [actionIncident, setActionIncident] = useState<SafetyRow | null>(null);
  const [review, setReview] = useState<ReliabilityReviewRow | null>(null);
  const requestSequence = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++requestSequence.current;
    setLoading(true);
    setError('');
    try {
      let result: ViewData;
      switch (tab) {
        case 'overview':
          result = await rpc<OverviewData>('admin_dashboard_overview', { p_days: days });
          break;
        case 'users':
          result = await rpc<UsersData>('admin_users', { p_search: searchQuery || null, p_limit: USERS_PAGE_SIZE, p_offset: userOffset });
          break;
        case 'marketplace':
          result = await rpc<MarketplaceData>('admin_marketplace_summary', { p_days: days });
          break;
        case 'product': {
          // Raw usage events live 90 days, so engagement never reads further back.
          const [engagement, feedback] = await Promise.all([
            rpc<ProductEngagementData>('admin_product_engagement', { p_days: Math.min(days, 90) }),
            rpc<ProductFeedbackData>('admin_product_feedback', { p_days: days, p_status: null, p_limit: 100 }),
          ]);
          result = { generatedAt: engagement.generatedAt, engagement, feedback };
          break;
        }
        case 'reliability': {
          const [metrics, reviewQueue, employer, anomaly] = await Promise.all([
            rpc<Omit<ReliabilityData, 'reviewQueue' | 'employer' | 'anomaly'>>('admin_reliability_summary', { p_days: days }),
            rpc<ReliabilityData['reviewQueue']>('admin_reliability_review_queue', { p_status: 'open', p_limit: 50 }),
            rpc<ReliabilityData['employer']>('admin_employer_reliability_summary', { p_days: Math.max(days, 30) }),
            rpc<ReliabilityData['anomaly']>('admin_anti_commercial_summary', { p_days: days }),
          ]);
          result = { ...metrics, reviewQueue, employer, anomaly };
          break;
        }
        case 'safety':
          result = await rpc<SafetyData>('admin_safety_queue', { p_limit: 50 });
          break;
        case 'operations':
          result = await rpc<OperationsData>('admin_operations_summary');
          break;
        case 'integrations':
          result = await rpc<IntegrationsData>('admin_integrations_summary', { p_hours: integrationHours });
          break;
        case 'audit':
          result = await rpc<AuditData>('admin_audit_log', { p_limit: 100 });
          break;
      }
      if (requestId === requestSequence.current) {
        setData((current) => ({ ...current, [tab]: result }));
      }
    } catch (caught) {
      if (requestId === requestSequence.current) {
        setError(caught instanceof Error ? caught.message : 'Unknown dashboard error');
      }
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  }, [days, integrationHours, searchQuery, tab, userOffset]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (tab !== 'integrations') return;
    // Every reporting RPC appends a row to private.admin_audit_events, so an
    // unattended tab polling once a minute writes ~1,440 audit rows a day and
    // pushes real privileged actions out of the 100-row audit view. Poll only
    // while someone is actually looking, and re-read once on return.
    let timer: number | undefined;
    const start = () => { timer ??= window.setInterval(() => void load(), 60_000); };
    const stop = () => { if (timer !== undefined) { window.clearInterval(timer); timer = undefined; } };
    const onVisibility = () => {
      if (document.hidden) stop();
      else { void load(); start(); }
    };
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => { stop(); document.removeEventListener('visibilitychange', onVisibility); };
  }, [load, tab]);

  const current = data[tab];
  const generatedAt = current?.generatedAt;

  function changeTab(next: TabId) {
    setTab(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand-lockup sidebar-brand">
          <div className="brand-mark">C</div>
          <div><strong>CoMiles</strong><span>Operations Console</span></div>
        </div>
        <nav aria-label="Dashboard sections">
          {nav.map((item) => (
            <button key={item.id} className={tab === item.id ? 'active' : ''} onClick={() => changeTab(item.id)}>
              <span aria-hidden="true">{item.icon}</span>{item.label}
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="security-chip"><span />MFA protected</div>
          <button className="signout-button" onClick={() => void supabase.auth.signOut()}>Sign out</button>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="topbar">
          <div>
            <span className="eyebrow">CoMiles / {nav.find((item) => item.id === tab)?.label}</span>
            <h1>{titleFor(tab)}</h1>
            <p>{subtitleFor(tab)}</p>
          </div>
          <div className="topbar-actions">
            {(tab === 'overview' || tab === 'marketplace' || tab === 'product' || tab === 'reliability') && (
              <label className="period-select">Period
                <select value={days} onChange={(event) => setDays(Number(event.target.value))}>
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                </select>
              </label>
            )}
            {tab === 'integrations' && (
              <label className="period-select">Health window
                <select value={integrationHours} onChange={(event) => setIntegrationHours(Number(event.target.value))}>
                  <option value={6}>Last 6 hours</option>
                  <option value={24}>Last 24 hours</option>
                  <option value={72}>Last 3 days</option>
                  <option value={168}>Last 7 days</option>
                </select>
              </label>
            )}
            <button className="refresh-button" onClick={() => void load()} disabled={loading}>
              <span>{loading ? 'Refreshing…' : 'Refresh'}</span>
            </button>
            {/* The sidebar footer is hidden below 620px, so this is the only
                sign-out reachable on a phone. */}
            <button className="compact-signout" onClick={() => void supabase.auth.signOut()}>Sign out</button>
          </div>
        </header>

        <div className="freshness">
          <span className={error ? 'bad' : ''} />
          {error ? 'Data unavailable' : generatedAt ? `Updated ${formatDate(generatedAt, true)} IST` : 'Loading source data'}
        </div>

        {loading && !current ? <LoadingBlock /> : error && !current ? (
          <ErrorBlock message={error} retry={() => void load()} />
        ) : (
          <>
            {error && current && <ErrorBlock message={`Showing the last successful snapshot. ${error}`} retry={() => void load()} />}
            {tab === 'overview' && current && <OverviewView data={current as OverviewData} />}
            {tab === 'users' && current && (
              <UsersView data={current as UsersData} search={search} setSearch={setSearch} loading={loading}
                submit={() => { setUserOffset(0); setSearchQuery(search.trim()); }}
                clear={() => { setUserOffset(0); setSearch(''); setSearchQuery(''); }}
                page={setUserOffset} />
            )}
            {tab === 'marketplace' && current && <MarketplaceView data={current as MarketplaceData} />}
            {tab === 'product' && current && <ProductView data={current as ProductData} reload={() => void load()} />}
            {tab === 'reliability' && current && <ReliabilityView data={current as ReliabilityData} review={setReview} />}
            {tab === 'safety' && current && (
              <SafetyView data={current as SafetyData} act={setActionIncident} />
            )}
            {tab === 'operations' && current && <OperationsView data={current as OperationsData} />}
            {tab === 'integrations' && current && <IntegrationsView data={current as IntegrationsData} />}
            {tab === 'audit' && current && <AuditView data={current as AuditData} />}
          </>
        )}
      </main>
      {actionIncident && (
        <SafetyAction incident={actionIncident} close={() => setActionIncident(null)}
          completed={() => { setActionIncident(null); void load(); }} />
      )}
      {review && (
        <ReliabilityDecision review={review} close={() => setReview(null)}
          completed={() => { setReview(null); void load(); }} />
      )}
    </div>
  );
}

function titleFor(tab: TabId) {
  return {
    overview: 'Network overview',
    users: 'User operations',
    marketplace: 'Marketplace health',
    product: 'Product use and feedback',
    reliability: 'Reliability and recovery',
    safety: 'Safety response',
    operations: 'System operations',
    integrations: 'Integration health',
    audit: 'Access audit',
  }[tab];
}

function subtitleFor(tab: TabId) {
  return {
    overview: 'The highest-signal view of commuter growth, shared journeys and guardrails.',
    users: 'Privacy-minimised account lookup and lifecycle context.',
    marketplace: 'Supply, demand, conversion and corridor health.',
    product: 'How the app is used, where members stop, and what they tell us. No member is named.',
    reliability: 'Explainable commitment outcomes, passenger recovery and accountable human review.',
    safety: 'Prioritised incidents with accountable response controls.',
    operations: 'Notification delivery, active rides and deletion workflow health.',
    integrations: 'One privacy-safe window for provider health, monitoring coverage and first-response diagnostics.',
    audit: 'An append-only record of sensitive dashboard access and actions.',
  }[tab];
}

const FEEDBACK_STATUSES: FeedbackStatus[] = ['new', 'reviewing', 'planned', 'closed'];

function seconds(value: number | null) {
  if (value === null) return '—';
  return value >= 60 ? `${Math.floor(value / 60)}m ${Math.round(value % 60)}s` : `${value}s`;
}

function rate(part: number | null, whole: number) {
  return part === null ? '—' : whole ? `${Math.round((100 * part) / whole)}%` : '—';
}

function ProductView({ data, reload }: { data: ProductData; reload: () => void }) {
  const e = data.engagement;
  const f = data.feedback;
  const nps = f.satisfaction;
  const [savingId, setSavingId] = useState<string | null>(null);
  const [triageError, setTriageError] = useState('');

  async function setStatus(id: string, status: FeedbackStatus) {
    setSavingId(id);
    setTriageError('');
    try {
      await rpc<string>('admin_set_feedback_status', { p_feedback_id: id, p_status: status });
      reload();
    } catch (caught) {
      setTriageError(caught instanceof Error ? caught.message : 'Status not saved');
    } finally {
      setSavingId(null);
    }
  }

  return (
    <div className="view-stack">
      <div className="metric-grid">
        <MetricCard label="Active today" value={formatNumber(e.activeMembers.today)} note={`${formatNumber(e.activeMembers.last7Days)} in 7 days · ${formatNumber(e.activeMembers.last30Days)} in 30`} tone="good" />
        <MetricCard label="Sessions" value={formatNumber(e.sessions)} note={`Median ${seconds(e.medianSessionSeconds)} in the foreground`} />
        <MetricCard label="Went quiet" value={formatNumber(e.wentQuiet.members)} note="Last seen in this window, nothing for 14 days" tone={e.wentQuiet.members ? 'warn' : 'default'} />
        <MetricCard label="Likely uninstalled" value={formatNumber(e.likelyUninstalled.members)} note={`Every push installation retired · ${formatNumber(e.likelyUninstalled.installationsRetired)} installations`} tone={e.likelyUninstalled.members ? 'warn' : 'default'} />
        <MetricCard label="Recommend score" value={nps.nps === null ? '—' : formatNumber(nps.nps)} note={nps.nps === null ? `Needs ${nps.minimumResponses} answers · ${nps.responses} so far` : `${nps.responses} answers · ${nps.promoters} promoters, ${nps.detractors} detractors`} />
        <MetricCard label="New feedback" value={formatNumber(f.feedbackByStatus.new ?? 0)} note={`${formatNumber(f.feedback.length)} in this period`} tone={f.feedbackByStatus.new ? 'warn' : 'good'} />
      </div>
      <p className="privacy-note">
        Usage is collected only from members on Privacy {e.collection.disclosureVersion} or later who have not switched it off
        ({formatNumber(e.collection.optedOut)} have). Screens, cohorts and reasons with fewer than {e.minimumCell} members are hidden.
        &ldquo;Likely uninstalled&rdquo; is inferred from Expo retiring every push token a member had; App Store Connect and Play Console report store-side deletions.
      </p>

      <div className="two-column">
        <Panel title="Daily active members" subtitle="Distinct members with any usage event, India Standard Time">
          <BarList rows={e.daily.slice(-14).map((row) => ({ label: formatDate(row.day), value: row.active_members }))} />
        </Panel>
        <Panel title="Where quiet members left off" subtitle="The last screen of members who then went 14 days without opening the app">
          <BarList rows={e.wentQuiet.lastScreens.map((row) => ({ label: row.screen_name, value: row.members }))} />
        </Panel>
      </div>

      <div className="two-column">
        <Panel title="Most used screens" subtitle="Screen views, by route name">
          <BarList rows={e.topScreens.map((row) => ({ label: `${row.screen_name} · ${formatNumber(row.members)} members`, value: row.views }))} />
        </Panel>
        <Panel title="Return rate by first week" subtitle="Share of new members who opened the app again on day 1, 7 and 30">
          <div className="table-wrap">
            <table>
              <thead><tr><th>First seen</th><th>Members</th><th>Day 1</th><th>Day 7</th><th>Day 30</th></tr></thead>
              <tbody>{e.retention.map((row) => (
                <tr key={row.cohortWeek}><td>{formatDate(row.cohortWeek)}</td><td>{formatNumber(row.members)}</td>
                  <td>{rate(row.day1, row.members)}</td><td>{rate(row.day7, row.members)}</td><td>{rate(row.day30, row.members)}</td></tr>
              ))}</tbody>
            </table>
            {!e.retention.length && <EmptyState label="No cohort large enough yet" />}
          </div>
        </Panel>
      </div>

      <div className="two-column">
        <Panel title="Platforms" subtitle="Distinct members in this window">
          <BarList rows={e.platforms.map((row) => ({ label: row.platform, value: row.members }))} />
        </Panel>
        <Panel title="App builds" subtitle="Which releases members are actually running">
          <BarList rows={e.builds.map((row) => ({ label: row.build, value: row.members }))} />
        </Panel>
      </div>

      <div className="two-column">
        <Panel title="Why members recommend, or don't" subtitle="Optional reason given with the 0–10 answer">
          <BarList rows={nps.reasons.map((row) => ({ label: friendly(row.reason_code), value: row.total }))} />
        </Panel>
        <Panel title="Why members deleted their account" subtitle="Optional and stored without any link to the account">
          <BarList rows={f.exitReasons.map((row) => ({ label: friendly(row.reason_code), value: row.members }))} />
        </Panel>
      </div>

      <Panel title="Feedback inbox" subtitle="Newest first. The sender is never shown; replies happen only if a member also emails support. Every status change is audited.">
        {triageError && <ErrorBlock message={triageError} retry={() => setTriageError('')} />}
        <div className="table-wrap">
          <table>
            <thead><tr><th>Received</th><th>Type</th><th>Feedback</th><th>App</th><th>Status</th></tr></thead>
            <tbody>{f.feedback.map((row) => (
              <tr key={row.id}>
                <td>{formatDate(row.created_at, true)}</td>
                <td><StatusPill value={row.category} /></td>
                <td className="feedback-copy">{row.message}</td>
                <td>{row.app_platform} {row.app_build}</td>
                <td>
                  <select aria-label="Feedback status" value={row.status} disabled={savingId === row.id}
                    onChange={(event) => void setStatus(row.id, event.target.value as FeedbackStatus)}>
                    {FEEDBACK_STATUSES.map((status) => <option key={status} value={status}>{friendly(status)}</option>)}
                  </select>
                </td>
              </tr>
            ))}</tbody>
          </table>
          {!f.feedback.length && <EmptyState label="No feedback in this period" />}
        </div>
      </Panel>
    </div>
  );
}

function OverviewView({ data }: { data: OverviewData }) {
  const k = data.kpis;
  return (
    <div className="view-stack">
      <div className="metric-grid">
        <MetricCard label="Total users" value={formatNumber(k.totalUsers)} note={`+${formatNumber(k.newUsers)} this period`} />
        <MetricCard label="Active commuters" value={formatNumber(k.activeCommuters)} note="Posted or requested a ride" tone="good" />
        <MetricCard label="Completed journeys" value={formatNumber(k.completedJourneys)} note="Passenger bookings completed" tone="good" />
        <MetricCard label="Booking acceptance" value={formatNumber(k.bookingAcceptancePct, '%')} note="Accepted, picked up or completed" />
        <MetricCard label="Rides posted" value={formatNumber(k.ridesPosted)} note={`Last ${data.windowDays} days`} />
        <MetricCard label="Open safety incidents" value={formatNumber(k.openSafetyIncidents)} note="Needs response or closure" tone={k.openSafetyIncidents ? 'warn' : 'good'} />
        <MetricCard label="Notification attention" value={formatNumber(k.pendingNotifications)} note="Pending, processing or failed" tone={k.pendingNotifications ? 'warn' : 'good'} />
      </div>
      <div className="two-column wide-left">
        <Panel title="Network movement" subtitle="Daily lifecycle outcomes in India Standard Time">
          <LineChart rows={data.trend} />
        </Panel>
        <Panel title="Booking funnel" subtitle="Requests by current lifecycle state">
          <BarList rows={data.bookingFunnel.map((row) => ({ label: row.status, value: row.count }))} />
        </Panel>
      </div>
      <div className="insight-strip">
        <div><span>Primary outcome</span><strong>Successful shared passenger journeys</strong></div>
        <p>Each completed passenger booking counts once. Driver-only rides are excluded, keeping the metric tied to shared value.</p>
      </div>
    </div>
  );
}

function UsersView(props: {
  data: UsersData; search: string; setSearch: (value: string) => void; submit: () => void; clear: () => void;
  page: (offset: number) => void; loading: boolean;
}) {
  const { total, limit, offset, rows } = props.data;
  const from = rows.length ? offset + 1 : 0;
  const to = offset + rows.length;
  const hasPrev = offset > 0;
  const hasNext = to < total;
  return (
    <div className="view-stack">
      <Panel title="Account directory" subtitle={`Showing ${formatNumber(from)}–${formatNumber(to)} of ${formatNumber(total)} matching accounts. Contact and exact-location data are excluded.`}
        action={<form className="search-form" onSubmit={(event) => { event.preventDefault(); props.submit(); }}>
          <input value={props.search} onChange={(event) => props.setSearch(event.target.value)}
            placeholder="Name, UUID or employer" aria-label="Search users" />
          <button className="secondary-button" type="submit">Search</button>
          {props.search && <button className="text-button" type="button" onClick={props.clear}>Clear</button>}
        </form>}>
        <div className="table-wrap">
          <table>
            <thead><tr><th>User</th><th>Joined</th><th>Trust</th><th>Rides</th><th>Bookings</th><th>Completed</th><th>Status</th></tr></thead>
            <tbody>
              {props.data.rows.map((user) => (
                <tr key={user.id}>
                  <td><div className="user-cell"><strong>{user.full_name}</strong><span>{user.id.slice(0, 8)} · {user.auth_provider}</span>{user.employer && <small>{user.employer}</small>}</div></td>
                  <td>{formatDate(user.created_at)}</td>
                  <td><div className="trust-list">{user.employer_verified && <span>Work verified</span>}{user.provider_verified_vehicle && <span>Vehicle verified</span>}{!user.employer_verified && !user.provider_verified_vehicle && 'None'}</div></td>
                  <td>{formatNumber(user.rides_posted)}</td>
                  <td>{formatNumber(user.bookings_requested)}</td>
                  <td>{formatNumber(user.journeys_completed)}</td>
                  <td><StatusPill value={user.is_banned ? 'suspended' : 'active'} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && <EmptyState label="No matching users" />}
        </div>
        {(hasPrev || hasNext) && (
          <nav className="pager" aria-label="Account directory pages">
            <button className="secondary-button" disabled={!hasPrev || props.loading}
              onClick={() => props.page(Math.max(0, offset - limit))}>Previous</button>
            <span>Page {Math.floor(offset / limit) + 1} of {Math.max(1, Math.ceil(total / limit))}</span>
            <button className="secondary-button" disabled={!hasNext || props.loading}
              onClick={() => props.page(offset + limit)}>Next</button>
          </nav>
        )}
      </Panel>
    </div>
  );
}

function MarketplaceView({ data }: { data: MarketplaceData }) {
  return (
    <div className="view-stack">
      <div className="metric-grid compact-metrics">
        <MetricCard label="Seat utilisation" value={formatNumber(data.seatUtilizationPct, '%')} note="Posted capacity claimed" />
        <MetricCard label="Active ride alerts" value={formatNumber(data.activeRideAlerts)} note="Unexpired one-shot alerts" />
        <MetricCard label="Unmet commute intents" value={formatNumber(data.activeUnmetIntents)} note="Active coarse demand records" />
      </div>
      <div className="two-column">
        <Panel title="Ride supply" subtitle="Posted rides by lifecycle state"><BarList rows={data.rideStatus} /></Panel>
        <Panel title="Booking outcomes" subtitle="Requests by lifecycle state"><BarList rows={data.bookingStatus} /></Panel>
      </div>
      <div className="two-column wide-left">
        <Panel title="Top coarse corridors" subtitle="Locality-level discovery labels; exact addresses stay private">
          <div className="corridor-list">
            {data.topCorridors.map((row, index) => (
              <div key={`${row.origin_label}-${row.destination_label}`}><span>{index + 1}</span>
                <p><strong>{row.origin_label}</strong><i>to</i><strong>{row.destination_label}</strong></p>
                <b>{formatNumber(row.rides)}</b>
              </div>
            ))}
            {!data.topCorridors.length && <EmptyState label="No corridor activity in this period" />}
          </div>
        </Panel>
        <Panel title="Cab-pool fallback" subtitle="CoMiles coordination states; provider booking remains external">
          <BarList rows={data.cabPoolStatus} />
        </Panel>
      </div>
    </div>
  );
}

function ReliabilityView({ data, review }: { data: ReliabilityData; review: (row: ReliabilityReviewRow) => void }) {
  const k = data.kpis;
  return (
    <div className="view-stack">
      <div className="metric-grid">
        <MetricCard label="Completion" value={formatNumber(k.completionPct, '%')} note={`${formatNumber(k.completedBookings)} of ${formatNumber(k.committedBookings)} committed bookings`} tone="good" />
        <MetricCard label="Passenger recovery" value={formatNumber(k.recoveryPct, '%')} note={`${formatNumber(k.recoveredPassengers)} of ${formatNumber(k.affectedPassengers)} affected passengers`} tone="good" />
        <MetricCard label="Late disruptions" value={formatNumber(k.lateDisruptions)} note={`${formatNumber(k.countableEvents)} currently countable`} />
        <MetricCard label="Open human reviews" value={formatNumber(k.openAppeals + k.openPickupDisputes)} note={`${formatNumber(k.openAppeals)} appeals · ${formatNumber(k.openPickupDisputes)} pickups`} tone={k.openAppeals + k.openPickupDisputes ? 'warn' : 'good'} />
        <MetricCard label="Appeal overturn rate" value={formatNumber(k.appealOverturnPct, '%')} note={`${formatNumber(k.appealsSubmitted)} submitted in this window`} />
        <MetricCard label="Median appeal time" value={k.medianAppealHours == null ? '—' : `${formatNumber(k.medianAppealHours)} h`} note="Resolved appeals only" />
        <MetricCard label="Active offer pauses" value={formatNumber(k.activeOfferPauses)} note={`${formatNumber(k.heldOpenSeats)} future open seats hidden by current holds`} tone={k.activeOfferPauses ? 'warn' : 'good'} />
        <MetricCard label="Volume signals" value={formatNumber(data.anomaly.flaggedDrivers)} note="Shadow monitoring only — never auto-enforced" />
      </div>

      <Panel title="Reviews needing a decision" subtitle="Oldest first. Queue reads and every decision are MFA-gated and appended to the access audit.">
        <p className="privacy-note">{k.measurementNote} Held seats measure current supply exposure; they do not establish that restrictions caused supply loss.</p>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Type</th><th>Member</th><th>Journey</th><th>Issue</th><th>Submitted</th><th>Status</th><th /></tr></thead>
            <tbody>{data.reviewQueue.rows.map((row) => (
              <tr key={`${row.kind}-${row.id}`}>
                <td><StatusPill value={row.kind} /></td>
                <td><div className="user-cell"><strong>{row.member_name ?? 'Deleted account'}</strong><span>{row.member_id?.slice(0, 8)}</span></div></td>
                <td><div className="user-cell"><strong>Ride {row.ride_id.slice(0,8)}</strong><span>{formatDate(row.departure_at, true)}</span></div></td>
                <td>{friendly(row.issue)}</td><td>{formatDate(row.created_at, true)}</td><td><StatusPill value={row.status} /></td>
                <td><button className="table-action" onClick={() => review(row)}>Review evidence</button></td>
              </tr>
            ))}</tbody>
          </table>
          {!data.reviewQueue.rows.length && <EmptyState label="No reliability reviews are waiting" />}
        </div>
      </Panel>

      <div className="two-column">
        <Panel title="Evaluation outcomes" subtitle="Latest state for each qualifying commitment event"><BarList rows={data.evaluationStates} /></Panel>
        <Panel title="Anti-commercial-use signals" subtitle="Observed volume anomalies; no ranking or eligibility action">
          <BarList rows={data.anomaly.signals} />
          <p className="privacy-note">Mode: <strong>shadow</strong>. These signals require contextual review and cannot suspend an account or pause offers.</p>
        </Panel>
      </div>

      <Panel title="Employer-pilot aggregates" subtitle={`Only cohorts with at least ${data.employer.minimumCohort} active members appear. No person, route, appeal, rating or disciplinary row is exposed.`}>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Organisation</th><th>Members</th><th>Committed</th><th>Completed</th><th>Completion</th><th>Late disruptions</th><th>Recovered</th></tr></thead>
            <tbody>{data.employer.rows.map((row) => <tr key={row.organization_id}>
              <td><strong>{row.name}</strong></td><td>{formatNumber(row.member_count)}</td><td>{formatNumber(row.committed_bookings)}</td>
              <td>{formatNumber(row.completed_bookings)}</td><td>{formatNumber(row.completion_pct, '%')}</td><td>{formatNumber(row.late_disruptions)}</td><td>{formatNumber(row.recovered_passengers)}</td>
            </tr>)}</tbody>
          </table>
          {!data.employer.rows.length && <EmptyState label="No employer cohort meets the privacy threshold" />}
        </div>
      </Panel>
    </div>
  );
}

function SafetyView({ data, act }: { data: SafetyData; act: (row: SafetyRow) => void }) {
  return (
    <div className="view-stack">
      <Panel title="Incident queue" subtitle="Critical and high-severity cases are prioritised before recency.">
        <div className="table-wrap">
          <table>
            <thead><tr><th>Severity</th><th>Case</th><th>Reporter</th><th>Subject</th><th>SLA</th><th>Events</th><th>Status</th><th /></tr></thead>
            <tbody>
              {data.rows.map((row) => {
                const overdue = !row.acknowledged_at && new Date(row.acknowledgement_due_at) < new Date();
                return <tr key={row.id}>
                  <td><StatusPill value={row.severity} /></td>
                  <td><div className="user-cell"><strong>{row.id.slice(0, 8)}</strong><span>{formatDate(row.created_at, true)}</span></div></td>
                  <td>{row.reporter_name ?? row.reporter_id?.slice(0, 8) ?? 'Deleted account'}</td>
                  <td>{row.subject_name ?? row.subject_id?.slice(0, 8) ?? 'Not specified'}</td>
                  <td className={overdue ? 'danger-text' : ''}>{row.acknowledged_at ? 'Acknowledged' : overdue ? 'Overdue' : formatDate(row.acknowledgement_due_at, true)}</td>
                  <td>{row.event_count}</td>
                  <td><StatusPill value={row.status} /></td>
                  <td>{row.subject_id && ['high','critical'].includes(row.severity) && <button className="table-action" onClick={() => act(row)}>Driving hold / review</button>}</td>
                </tr>;
              })}
            </tbody>
          </table>
          {!data.rows.length && <EmptyState label="No safety incidents recorded" />}
        </div>
      </Panel>
    </div>
  );
}

function OperationsView({ data }: { data: OperationsData }) {
  return (
    <div className="view-stack">
      <div className="metric-grid compact-metrics">
        <MetricCard label="Active rides" value={formatNumber(data.activeRides)} note="Currently in progress" />
        <MetricCard label="Stale worker claims" value={formatNumber(data.staleClaimedNotifications)}
          note="Processing for more than 5 minutes" tone={data.staleClaimedNotifications ? 'warn' : 'good'} />
      </div>
      <div className="three-column">
        <Panel title="Notification outbox"><BarList rows={data.outbox} /></Panel>
        <Panel title="Push receipts · 7 days"><BarList rows={data.receipts7d} /></Panel>
        <Panel title="Deletion jobs"><BarList rows={data.deletionJobs} /></Panel>
      </div>
      <Panel title="Recent notification failures" subtitle="Payloads and recipient identity are excluded from this view.">
        <div className="table-wrap">
          <table>
            <thead><tr><th>Event</th><th>Error</th><th>Attempts</th><th>Created</th><th>Next retry</th></tr></thead>
            <tbody>{data.recentNotificationFailures.map((row) => (
              <tr key={row.id}><td><code>{row.event_type}</code></td><td className="error-copy">{row.last_error}</td>
                <td>{row.attempts}</td><td>{formatDate(row.created_at, true)}</td><td>{formatDate(row.next_attempt_at, true)}</td></tr>
            ))}</tbody>
          </table>
          {!data.recentNotificationFailures.length && <EmptyState label="No recent notification failures" />}
        </div>
      </Panel>
    </div>
  );
}

function IntegrationsView({ data }: { data: IntegrationsData }) {
  const [category, setCategory] = useState('all');
  const [status, setStatus] = useState('all');
  const [query, setQuery] = useState('');
  const normalizedQuery = query.trim().toLowerCase();
  const categories = useMemo(() => [...new Set(data.rows.map((row) => row.category))].sort(), [data.rows]);
  const filteredRows = useMemo(() => data.rows.filter((row) => {
    if (category !== 'all' && row.category !== category) return false;
    if (status === 'attention' && !['down', 'degraded'].includes(row.status)) return false;
    if (status === 'coverage' && !['unknown', 'stale', 'unmonitored'].includes(row.status)) return false;
    if (!['all', 'attention', 'coverage'].includes(status) && row.status !== status) return false;
    return !normalizedQuery || `${row.name} ${row.category} ${row.diagnosis} ${row.lastErrorCode ?? ''}`.toLowerCase().includes(normalizedQuery);
  }), [category, data.rows, normalizedQuery, status]);
  const summary = data.summary;
  const operational = summary.healthy + summary.observed;

  return (
    <div className="view-stack">
      <div className="metric-grid integration-metrics">
        <MetricCard label="Enabled integrations" value={formatNumber(summary.total)} note="Provider and platform surfaces in scope" />
        <MetricCard label="Healthy or observed" value={formatNumber(operational)} note="Healthy telemetry or successful auth activity" tone="good" />
        <MetricCard label="Needs attention" value={formatNumber(summary.attention)} note="Degraded or down in this window" tone={summary.attention ? 'warn' : 'good'} />
        <MetricCard label="Coverage gaps" value={formatNumber(summary.blind_spots)} note="Unknown, stale or manually verified" tone={summary.blind_spots ? 'warn' : 'good'} />
      </div>

      <div className={`health-summary ${summary.attention ? 'attention' : 'clear'}`} role="status">
        <div>
          <span className="health-summary-dot" />
          <strong>{summary.attention ? `${summary.attention} integration${summary.attention === 1 ? '' : 's'} need attention` : 'No monitored provider outage detected'}</strong>
        </div>
        <p>Unknown and unmonitored states are coverage gaps, not proof of an outage. Real provider traffic is observed without sending diagnostic SMS, email, or billable VAHAN requests.</p>
      </div>

      <Panel title="Integration control plane" subtitle={`Health evidence from the last ${data.windowHours} hours. Auto-refreshes every 60 seconds.`}
        action={<div className="integration-filters">
          <input aria-label="Search integrations" placeholder="Search provider or error" value={query} onChange={(event) => setQuery(event.target.value)} />
          <select aria-label="Filter integration category" value={category} onChange={(event) => setCategory(event.target.value)}>
            <option value="all">All categories</option>
            {categories.map((value) => <option key={value} value={value}>{friendly(value)}</option>)}
          </select>
          <select aria-label="Filter integration status" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="all">All statuses</option>
            <option value="attention">Needs attention</option>
            <option value="coverage">Coverage gaps</option>
            <option value="healthy">Healthy</option>
            <option value="observed">Observed</option>
            <option value="disabled">Disabled</option>
          </select>
        </div>}>
        <div className="table-wrap integration-table-wrap">
          <table className="integration-table">
            <thead><tr><th>Integration</th><th>Status</th><th>Signals</th><th>Last signal</th><th>Latency</th><th>Diagnosis</th></tr></thead>
            <tbody>
              {filteredRows.map((row) => <IntegrationTableRow key={row.id} row={row} />)}
            </tbody>
          </table>
          {!filteredRows.length && <EmptyState label="No integrations match these filters" />}
        </div>
      </Panel>

      <Panel title="Recent provider failures" subtitle="Sanitised error codes only; request payloads, identities, contacts, coordinates and secrets are never collected.">
        <div className="table-wrap">
          <table>
            <thead><tr><th>Provider</th><th>Operation</th><th>Error code</th><th>HTTP</th><th>Latency</th><th>Occurrences</th><th>Last seen</th></tr></thead>
            <tbody>{data.recentFailures.map((failure, index) => (
              <tr key={`${failure.integration_id}-${failure.operation}-${failure.observedAt}-${index}`}>
                <td><strong>{failure.name}</strong></td><td><code>{friendly(failure.operation)}</code></td>
                <td><code className="danger-text">{failure.errorCode ?? 'provider_error'}</code></td>
                <td>{failure.httpStatus ?? '—'}</td><td>{formatLatency(failure.latencyMs)}</td>
                <td>{formatNumber(failure.failureCount)}</td><td>{formatDate(failure.observedAt, true)}</td>
              </tr>
            ))}</tbody>
          </table>
          {!data.recentFailures.length && <EmptyState label="No provider failures recorded in this window" />}
        </div>
      </Panel>
    </div>
  );
}

function IntegrationTableRow({ row }: { row: IntegrationRow }) {
  const signals = row.successCount + row.failureCount;
  return (
    <tr>
      <td data-label="Integration"><div className="integration-name"><strong>{row.name}</strong><span>{friendly(row.category)} · {friendly(row.criticality)} criticality</span></div></td>
      <td data-label="Status"><StatusPill value={row.status} /></td>
      <td data-label="Signals"><div className="signal-cell"><strong>{signals ? `${formatNumber(row.successRatePct, '%')}` : 'No data'}</strong><span>{formatNumber(signals)} signal{signals === 1 ? '' : 's'}</span></div></td>
      <td data-label="Last signal"><div className="signal-cell"><strong>{formatDate(row.lastCheckedAt, true)}</strong><span>{row.lastOperation ? friendly(row.lastOperation) : friendly(row.monitoringKind)}</span></div></td>
      <td data-label="Latency"><div className="signal-cell"><strong>{formatLatency(row.lastLatencyMs)}</strong><span>P95 {formatLatency(row.p95LatencyMs)}</span></div></td>
      <td data-label="Diagnosis" className="diagnosis-cell">
        <p>{row.diagnosis}</p>
        <details><summary>Diagnose</summary>
          <div className="diagnostic-detail">
            <div><span>Coverage</span><p>{row.coverageNote}</p></div>
            <div><span>Next checks</span><p>{row.runbook}</p></div>
            {row.lastErrorCode && <div><span>Latest error</span><code>{row.lastErrorCode}{row.lastHttpStatus ? ` · HTTP ${row.lastHttpStatus}` : ''}</code></div>}
          </div>
        </details>
      </td>
    </tr>
  );
}

function friendly(value: string) {
  return value.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatLatency(value: number | null | undefined) {
  if (value === null || value === undefined) return '—';
  return value >= 1000 ? `${(value / 1000).toFixed(1)} s` : `${formatNumber(value)} ms`;
}


function AuditView({ data }: { data: AuditData }) {
  return (
    <div className="view-stack">
      <Panel title="Sensitive access history" subtitle="Every dashboard data read and privileged account action is appended here.">
        <div className="timeline">
          {data.rows.map((row) => (
            <div className="timeline-row" key={row.id}>
              <span className="timeline-dot" />
              <div><strong>{row.action.replaceAll('.', ' / ')}</strong>
                <p>{row.admin_name ?? row.admin_id.slice(0, 8)}{row.target_type ? ` · ${row.target_type} ${row.target_id?.slice(0, 12) ?? ''}` : ''}</p>
              </div>
              <time>{formatDate(row.created_at, true)}</time>
            </div>
          ))}
          {!data.rows.length && <EmptyState label="No dashboard access recorded yet" />}
        </div>
      </Panel>
    </div>
  );
}

function ReliabilityDecision(props: { review: ReliabilityReviewRow; close: () => void; completed: () => void }) {
  const [decision, setDecision] = useState<'upheld' | 'declined'>('upheld');
  const [note, setNote] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const dialog = useRef<HTMLDialogElement | null>(null);

  useEffect(() => { dialog.current?.showModal(); }, []);

  async function submit() {
    if (note.trim().length < 20) return;
    setWorking(true); setError('');
    try {
      await rpc('admin_decide_reliability_review', {
        p_kind: props.review.kind,
        p_review: props.review.id,
        p_decision: decision,
        p_note: note.trim(),
      });
      props.completed();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Decision failed');
      setWorking(false);
    }
  }

  const evidence = Object.entries(props.review.evidence ?? {}).filter(([, value]) => value !== null && value !== undefined);
  return <dialog ref={dialog} className="modal review-modal native-modal" aria-labelledby="review-title"
    onCancel={(event) => { event.preventDefault(); if (!working) props.close(); }}>
      <button className="modal-close" onClick={props.close} disabled={working} aria-label="Close">×</button>
      <span className="eyebrow">{friendly(props.review.kind)} · {props.review.id.slice(0, 8)}</span>
      <h2 id="review-title">Review the recorded facts</h2>
      <p><strong>Ride {props.review.ride_id.slice(0,8)}</strong><br />Departure {formatDate(props.review.departure_at, true)} IST</p>
      {props.review.member_statement && <blockquote><strong>Member explanation</strong><p>{props.review.member_statement}</p></blockquote>}
      <dl className="evidence-grid">{evidence.map(([key, value]) => <div key={key}><dt>{friendly(key)}</dt><dd>{typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}</dd></div>)}</dl>
      <label>Decision<select value={decision} onChange={(event) => setDecision(event.target.value as 'upheld' | 'declined')}>
        <option value="upheld">Uphold the member’s claim or appeal</option>
        <option value="declined">Decline it based on the record</option>
      </select></label>
      <label>Evidence-based decision note<textarea value={note} onChange={(event) => setNote(event.target.value)} minLength={20} maxLength={1000}
        placeholder="Explain the decision for the member. Exclude reporter identity, private evidence, medical, identity and financial details." /></label>
      <small className="decision-warning">This decision is final in the console. It records your identity, appends an audit event, and notifies the affected member.</small>
      {error && <p className="inline-error" role="alert">{error}</p>}
      <div className="modal-actions"><button className="secondary-button" onClick={props.close} disabled={working}>Cancel</button>
        <button className="primary-button" disabled={working || note.trim().length < 20} onClick={() => void submit()}>{working ? 'Recording…' : 'Record decision'}</button></div>
  </dialog>;
}


